<template>
  <!--
    Seletor de OPERAÇÃO renderizado dentro da conversa.

    Diferente do seletor de cliente, aqui não há busca: a conversa já sabe de
    quem se trata, então o widget lista as operações daquele cliente e o
    operador só aponta qual. Perguntar "qual operação?" em campo aberto
    obrigaria a decorar o número.
  -->
  <div
    class="picker"
    data-test="clara-operation-picker"
  >
    <p class="picker__title">Qual operação de {{ customer.name }}?</p>

    <div
      v-if="loading"
      class="picker__hint"
    >
      Carregando operações...
    </div>

    <div
      v-else-if="!operations.length"
      class="picker__hint"
      data-test="clara-operation-picker-empty"
    >
      Nenhuma operação encontrada para este cliente.
    </div>

    <ul
      v-else
      class="picker__list"
    >
      <li
        v-for="operation in operations"
        :key="operation.id"
      >
        <button
          type="button"
          class="picker__option"
          data-test="clara-operation-picker-option"
          @click="$emit('select', operation)"
        >
          <span class="picker__option-head">
            <span class="picker__option-id">#{{ operation.id }}</span>
            <span
              v-if="operation.status"
              class="picker__option-status"
            >{{ operation.status }}</span>
            <span
              v-if="operation.date"
              class="picker__option-date"
            >{{ operation.date }}</span>
          </span>

          <!-- O que identifica a operação para o operador é o par de moedas, não
               o número: "o dólar de 10 mil" e como ele fala. Por isso a linha do
               fluxo (origem > destino) e o corpo do item, com bandeira e valor
               dos dois lados. -->
          <span
            class="picker__flow"
            data-test="clara-operation-picker-flow"
          >
            <span class="picker__leg">
              <img
                v-if="operation.origin.flag"
                :src="operation.origin.flag"
                :alt="operation.origin.code"
                class="picker__flag"
                data-test="clara-operation-picker-flag-origin"
              />
              <span class="picker__leg-code">{{ operation.origin.code }}</span>
              <span
                v-if="operation.origin.amount"
                class="picker__leg-amount"
              >{{ operation.origin.amount }}</span>
            </span>

            <PhArrowRight
              :size="11"
              class="picker__arrow"
            />

            <span class="picker__leg">
              <img
                v-if="operation.destination.flag"
                :src="operation.destination.flag"
                :alt="operation.destination.code"
                class="picker__flag"
                data-test="clara-operation-picker-flag-destination"
              />
              <span class="picker__leg-code">{{ operation.destination.code }}</span>
              <span
                v-if="operation.destination.amount"
                class="picker__leg-amount"
              >{{ operation.destination.amount }}</span>
            </span>
          </span>
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="picker__cancel"
      data-test="clara-operation-picker-cancel"
      @click="$emit('cancel')"
    >
      Cancelar
    </button>
  </div>
</template>

<script>
import OperationApi from '@/api/operation/operation-api';
import OperationFiltersModel from '@/model/operation-filters-model';
import mixinCurrencyFlag from '@/mixin/mixin-currency-flag';
import utilsStorage from '@/utils/storage';

/** Mais que isso vira rolagem dentro da conversa, que fica ruim de usar. */
const MAX_RESULTS = 8;

/** A perna nacional do câmbio é sempre o real — é ele que `totalValue` carrega. */
const LOCAL_CURRENCY = 'BRL';

export default {
  name: 'ClaraOperationPicker',

  mixins: [mixinCurrencyFlag],

  props: {
    customer: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      operations: [],
      loading: true
    };
  },

  created() {
    this.operationApi = new OperationApi(utilsStorage.getUserOnStorage());
    this.load();
  },

  methods: {
    async load() {
      try {
        const filtros = new OperationFiltersModel({
          customerId: this.customer.id,
          quantity: MAX_RESULTS,
          page: 1
        });

        const response = await this.operationApi.getOperations(filtros);

        this.operations = (response.data || []).slice(0, MAX_RESULTS).map(this.toOption);
      } catch (error) {
        /* Silencioso de propósito: o vocabulário da Clara proíbe falar em erro
           para o operador. Lista vazia já comunica o que ele precisa saber. */
        this.operations = [];
      } finally {
        this.loading = false;
      }
    },

    toOption(operation) {
      const foreign = this.toLeg(operation.currency?.code || '', operation.amount);
      const local = this.toLeg(LOCAL_CURRENCY, operation.totalValue);

      /* Compra (`exchangeBuy`) é o cliente RECEBENDO do exterior: a moeda
         estrangeira entra e vira real. Na venda o caminho é o inverso — ele
         paga em real para enviar. Inverter isso mostraria a operação ao
         contrário do que o operador vê na tela de câmbio. */
      const [origin, destination] = operation.exchangeBuy
        ? [foreign, local]
        : [local, foreign];

      return {
        id: operation.id,
        status: operation.status?.description || operation.status?.name || null,
        date: operation.createdAt
          ? new Date(operation.createdAt).toLocaleDateString('pt-BR')
          : '',
        origin,
        destination,
        customer: { id: this.customer.id, name: this.customer.name }
      };
    },

    toLeg(code, value) {
      return {
        code,
        flag: this.getCurrencyFlag(code),
        amount: this.formatAmount(value)
      };
    },

    /* Só o número: o código da moeda já aparece ao lado da bandeira, e o
       símbolo do Intl (US$, R$) repetiria a mesma informação em outra grafia. */
    formatAmount(value) {
      const amount = Number(value);

      if (!value || Number.isNaN(amount)) return '';

      return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style scoped>
.picker {
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-md);
  background: var(--tc-surface);
  padding: var(--tc-space-3);
  margin-bottom: var(--tc-space-3);
}

.picker__title {
  margin: 0 0 var(--tc-space-2);
  font-size: 0.85rem;
  font-weight: var(--tc-fw-semibold);
  color: var(--tc-text);
}

.picker__hint {
  font-size: 0.8rem;
  color: var(--tc-text-2);
  padding: var(--tc-space-2) 0;
}

.picker__list {
  list-style: none;
  margin: 0 0 var(--tc-space-2);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.picker__option {
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: var(--tc-space-2);
  border-radius: var(--tc-radius-sm);
  background: transparent;
  transition: background var(--tc-transition);
}

.picker__option:hover {
  background: var(--clara-tint);
}

.picker__option-head {
  display: flex;
  align-items: baseline;
  gap: var(--tc-space-2);
}

.picker__option-id {
  font-family: var(--tc-font-mono);
  font-size: 0.8rem;
  font-weight: var(--tc-fw-semibold);
  /* --clara-text, nao --clara-on-light: o tom fixo rende 2,45 sobre a
     superficie escura. O token de leitura troca junto com o tema. */
  color: var(--clara-text);
}

.picker__option-status {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: var(--tc-tracking-label);
  color: var(--tc-text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Empurrada para a direita: a data é o dado menos consultado da linha e não
   pode disputar espaço com o status quando o painel estreita. */
.picker__option-date {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.7rem;
  color: var(--tc-text-3);
}

.picker__flow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--tc-space-1) var(--tc-space-2);
}

.picker__leg {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.picker__flag {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.picker__leg-code {
  font-size: 0.7rem;
  font-weight: var(--tc-fw-semibold);
  letter-spacing: var(--tc-tracking-label);
  color: var(--tc-text-2);
}

.picker__leg-amount {
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  color: var(--tc-text);
}

.picker__arrow {
  flex-shrink: 0;
  color: var(--tc-text-2);
}

.picker__cancel {
  font-size: 0.78rem;
  color: var(--tc-text-2);
  padding: var(--tc-space-1) 0;
}

.picker__cancel:hover {
  color: var(--tc-text);
}
</style>
